// src/pages/ContactInfoPage.js
// Standalone contact info page with FULL tracking integration
// Captures contact info to Tracking events AND ViewReport documents

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getCountryCallingCode } from 'react-phone-number-input/input';
import { getSortedCountries, getCountryName, detectCountryFromLocale } from '../utils/phoneCountryUtils';
import { doc, setDoc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Tracking imports
import Tracking, {
  trackViewReportContactSubmission,
  trackViewReportContactButtonClick
} from '../utils/Tracking';
import { trackMetaLead } from '../utils/metaPixel';
import { trackTikTokContact } from '../utils/tikTokPixel';

import BulletList from '../components/Onboarding/BulletList';
import lockedIcon from '../images/optionIcons/locked.svg';
import WhatsAppLogo from '../images/WhatsAppLogo.png';
import matchMedicLogo from '../images/matchmedic_logo_white.png';

// ============================================
// CONTACT TRACKING UTILITIES
// ============================================

/**
 * Write contact submission directly to ViewReport document in Firestore.
 * This is a CRITICAL function - contact capture is TOP PRIORITY.
 */
async function writeContactToViewReport({ 
  viewReportId, 
  reportRequestId, 
  contactType, 
  phoneNumber, 
  dialCode, 
  phoneRegion, 
  emailAddress,
  source 
}) {
  if (!viewReportId || !reportRequestId) {
    console.warn('[ContactInfoPage] Cannot write to ViewReport - missing IDs:', { viewReportId, reportRequestId });
    return { success: false, error: 'Missing viewReportId or reportRequestId' };
  }

  try {
    const viewReportRef = doc(db, 'recommendations_form_data', reportRequestId, 'view_report', viewReportId);
    const timestamp = new Date().toISOString();

    const contactEntry = {
      type: contactType,
      value: contactType === 'phone' ? phoneNumber : emailAddress,
      countryCode: phoneRegion || null,
      dialCode: dialCode || null,
      submittedAt: timestamp,
      source: source || 'contact_info_page',
      validationPassed: true
    };

    const updateData = {
      lastUpdatedAt: serverTimestamp(),
      contactSubmissions: arrayUnion(contactEntry)
    };

    // Also update primary contact fields
    if (contactType === 'phone' && phoneNumber) {
      updateData.primaryContactPhoneNumber = phoneNumber;
      updateData.primaryContactDialCode = dialCode;
      updateData.primaryContactPhoneRegion = phoneRegion;
      updateData.primaryContactSource = 'contact_info_page';
      updateData.primaryContactCapturedAt = timestamp;
    }
    if (contactType === 'email' && emailAddress) {
      updateData.primaryContactEmailAddress = emailAddress;
      updateData.primaryContactSource = 'contact_info_page';
      updateData.primaryContactCapturedAt = timestamp;
    }

    await updateDoc(viewReportRef, updateData).catch(async (err) => {
      // Document might not exist yet - create it
      if (err.code === 'not-found') {
        console.log('[ContactInfoPage] ViewReport doc not found, creating...');
        await setDoc(viewReportRef, {
          ...updateData,
          createdAt: serverTimestamp(),
          reportRequestId,
          viewReportSessionId: viewReportId,
          contactSubmissions: [contactEntry]
        });
      } else {
        throw err;
      }
    });

    console.log('[ContactInfoPage] ✅ CONTACT WRITTEN TO FIRESTORE:', {
      viewReportId,
      reportRequestId,
      contactType,
      value: contactType === 'phone' ? phoneNumber : emailAddress
    });

    return { success: true };
  } catch (error) {
    console.error('[ContactInfoPage] ❌ FAILED TO WRITE CONTACT TO FIRESTORE:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Track contact submission to Tracking events
 */
async function trackContactSubmission({
  viewReportId,
  reportRequestId,
  contactType,
  phoneNumber,
  dialCode,
  phoneRegion,
  emailAddress,
  source
}) {
  try {
    // Track via ViewReport tracking (which also tracks to session events)
    const trackResult = await trackViewReportContactSubmission({
      viewReportId,
      reportRequestId,
      contactType,
      phoneNumber,
      dialCode,
      phoneRegion,
      emailAddress,
      source: source || 'contact_info_page'
    });

    console.log('[ContactInfoPage] ✅ CONTACT TRACKED TO SESSION:', {
      trackResult,
      contactType,
      value: contactType === 'phone' ? phoneNumber : emailAddress
    });

    return { success: true };
  } catch (error) {
    console.error('[ContactInfoPage] ❌ FAILED TO TRACK CONTACT:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Write button click to ViewReport document in Firestore.
 * Mirrors the functionality in ViewReportPage.js handleContactButtonClick.
 */
async function writeButtonClickToViewReport({
  viewReportId,
  reportRequestId,
  buttonType,
  href,
  pageName
}) {
  if (!viewReportId || !reportRequestId) {
    console.warn('[ContactInfoPage] Cannot write button click - missing IDs:', { viewReportId, reportRequestId });
    return { success: false, error: 'Missing viewReportId or reportRequestId' };
  }

  try {
    const viewReportRef = doc(db, 'recommendations_form_data', reportRequestId, 'view_report', viewReportId);
    const timestamp = new Date().toISOString();

    const clickEntry = {
      timestamp,
      buttonType,
      href,
      pageName,
    };

    await updateDoc(viewReportRef, {
      lastUpdatedAt: serverTimestamp(),
      contactButtonClicks: arrayUnion(clickEntry)
    }).catch(async (err) => {
      // Document might not exist yet - create it
      if (err.code === 'not-found') {
        console.log('[ContactInfoPage] ViewReport doc not found, creating...');
        await setDoc(viewReportRef, {
          createdAt: serverTimestamp(),
          lastUpdatedAt: serverTimestamp(),
          reportRequestId,
          viewReportSessionId: viewReportId,
          contactButtonClicks: [clickEntry]
        });
      } else {
        throw err;
      }
    });

    console.log('[ContactInfoPage] ✅ BUTTON CLICK WRITTEN TO FIRESTORE:', {
      viewReportId,
      reportRequestId,
      buttonType,
      pageName
    });

    return { success: true };
  } catch (error) {
    console.error('[ContactInfoPage] ❌ FAILED TO WRITE BUTTON CLICK:', error);
    return { success: false, error: error.message };
  }
}

// ============================================
// PHONE VIEW COMPONENT
// ============================================

function PhoneView({ onSwitchToEmail, viewReportId, reportRequestId }) {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState(() => detectCountryFromLocale());
  const [phoneInput, setPhoneInput] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);

  const { priority: priorityCountries, rest: restCountries } = getSortedCountries();

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const validatePhone = () => {
    setError('');
    if (!phoneInput || !phoneInput.trim()) {
      setError('Please enter your phone number.');
      return false;
    }
    const digitsOnly = phoneInput.replace(/\D/g, '');
    if (digitsOnly.length < 6) {
      setError('Please enter a valid phone number.');
      return false;
    }
    return true;
  };

  const handleContinue = useCallback(async () => {
    if (!validatePhone() || isSubmitting) return;
    
    setIsSubmitting(true);
    const dialCode = `+${getCountryCallingCode(selectedCountry)}`;
    const fullNumber = `${dialCode}${phoneInput}`;
    
    console.log('[ContactInfoPage] 📱 PHONE SUBMISSION STARTED:', {
      fullNumber,
      viewReportId,
      reportRequestId
    });

    // Track to both systems in parallel
    const [trackResult, firestoreResult] = await Promise.all([
      // 1. Track to session events (ALWAYS)
      trackContactSubmission({
        viewReportId,
        reportRequestId,
        contactType: 'phone',
        phoneNumber: fullNumber,
        dialCode,
        phoneRegion: selectedCountry,
        source: 'contact_info_page_phone'
      }),
      // 2. Write to ViewReport document (if we have IDs)
      viewReportId && reportRequestId 
        ? writeContactToViewReport({
            viewReportId,
            reportRequestId,
            contactType: 'phone',
            phoneNumber: fullNumber,
            dialCode,
            phoneRegion: selectedCountry,
            source: 'contact_info_page_phone'
          })
        : Promise.resolve({ success: false, skipped: true })
    ]);

    console.log('[ContactInfoPage] 📱 PHONE SUBMISSION COMPLETE:', {
      trackResult,
      firestoreResult
    });

    // Track pixel Lead events for phone submission
    trackMetaLead({ content_name: 'contact_info_page_phone', content_category: 'view_report_contact' });
    trackTikTokContact('view_report_phone');

    // Wait for async queue to flush before navigating
    await new Promise(resolve => setTimeout(resolve, 500));

    console.log('[ContactInfoPage] ✅ NAVIGATING TO HOMEPAGE');
    navigate('/');
    // Note: isSubmitting stays true to prevent double-clicks during navigation
  }, [phoneInput, selectedCountry, viewReportId, reportRequestId, isSubmitting, navigate]);

  // Track WhatsApp button click - writes to BOTH Firestore and tracking events
  const handleWhatsAppClick = useCallback(() => {
    console.log('[ContactInfoPage] 💬 WHATSAPP CLICK:', { viewReportId, reportRequestId });
    
    const href = 'https://wa.me/908503469160';
    const pageName = 'contact_info_page_phone';
    
    // 1. Write to ViewReport document (if we have IDs)
    if (viewReportId && reportRequestId) {
      writeButtonClickToViewReport({
        viewReportId,
        reportRequestId,
        buttonType: 'whatsapp',
        href,
        pageName,
      });
    }
    
    // 2. Track to session events
    trackViewReportContactButtonClick({
      viewReportId,
      reportRequestId,
      buttonType: 'whatsapp',
      pageName,
      href,
    });

    // 3. Track pixel Lead events for WhatsApp click
    trackMetaLead({ content_name: 'contact_info_page_whatsapp', content_category: 'view_report_contact' });
    trackTikTokContact('view_report_contact');
  }, [viewReportId, reportRequestId]);

  const disabled = !phoneInput || !phoneInput.trim() || isSubmitting;

  return (
    <>
      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="w-full lg:w-2/5 mx-auto px-5 sm:px-6 pt-6 pb-48">
          {/* Title & Subtitle */}
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 leading-tight">
            How do we contact you?
          </h1>
          <p className="text-gray-400 text-sm mb-8">
            We need your phone number to facilitate the process with the appropriate clinics.
          </p>

          {/* Phone Input Section */}
          <label className="block mb-2 text-sm font-medium text-white">
            Your phone number
          </label>

          <div className="flex gap-2 items-center">
            {/* Country Selector */}
            <div className="relative flex-shrink-0 flex items-center gap-1">
              <select
                value={selectedCountry}
                onChange={(e) => {
                  setSelectedCountry(e.target.value);
                  if (error) setError('');
                  if (inputRef.current) inputRef.current.focus();
                }}
                autoComplete="tel-country-code"
                className="phone-country-select h-12 w-12 cursor-pointer bg-transparent border-0 focus:outline-none appearance-none"
                style={{
                  backgroundImage: `url(https://purecatamphetamine.github.io/country-flag-icons/3x2/${selectedCountry}.svg)`,
                  backgroundSize: '36px 24px',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  color: 'transparent',
                }}
              >
                {priorityCountries.map((country) => (
                  <option key={country} value={country}>
                    {getCountryName(country)} +{getCountryCallingCode(country)}
                  </option>
                ))}
                <option disabled>──────────────</option>
                {restCountries.map((country) => (
                  <option key={country} value={country}>
                    {getCountryName(country)} +{getCountryCallingCode(country)}
                  </option>
                ))}
              </select>
              {/* Dropdown arrow */}
              <div className="pointer-events-none -ml-2">
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
                  <path 
                    d="M1 1L5 5L9 1" 
                    stroke="#e5e7eb" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>

            {/* Phone Input with Country Code Prefix */}
            <div className="flex-1 relative">
              <div className="flex items-center h-10 px-4 rounded-xl border bg-transparent border-gray-600 focus-within:ring-2 focus-within:ring-white transition-all">
                <span className="font-medium select-none pointer-events-none mr-1 text-gray-400">
                  +{getCountryCallingCode(selectedCountry)}
                </span>
                <input
                  ref={inputRef}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  name="phone-national"
                  value={phoneInput}
                  onChange={(e) => {
                    const cleaned = e.target.value.replace(/[^\d\s\-()]/g, '');
                    setPhoneInput(cleaned);
                    if (error) setError('');
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleContinue();
                    }
                  }}
                  placeholder="555 123 4567"
                  className="flex-1 bg-transparent border-none outline-none text-white placeholder-gray-500"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-2 text-sm text-red-500">{error}</div>
          )}

          {/* Bullet Points */}
          <div className="mt-7">
            <BulletList
              items={[
                (
                  <>
                    Your data will be secured with industry standard <strong>AES-256 encryption</strong>
                    <img
                      src={lockedIcon}
                      alt=""
                      className="inline-block ml-1 w-[0.9em] h-[0.8em] align-[-0.05em] opacity-90"
                    />
                  </>
                ),
                'We will never spam you',
                'Many clinics prefer consultation via mobile messaging apps',
                (
                  <>
                    To skip this step{' '}
                    <button
                      type="button"
                      className="font-medium cursor-pointer inline-flex items-center"
                      onClick={onSwitchToEmail}
                    >
                      <strong>click here</strong>
                    </button>
                    <span className="font-semibold"> →</span>
                  </>
                ),
              ]}
              theme="dark"
              delayMs={400}
            />
          </div>
        </div>
      </div>

      {/* Fixed Bottom Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-mm_black z-50">
        <div className="w-full lg:w-2/5 mx-auto px-5 sm:px-6 pt-4 pb-6 space-y-3">
          {/* WhatsApp secondary button */}
          <a
            href="https://wa.me/908503469160?text=Hi!%20I'm%20interested%20in%20your%20services."
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleWhatsAppClick}
            className="w-full py-3 px-6 rounded-full md:rounded-lg border border-white bg-[#1f1f1f] hover:bg-black transition inline-flex items-center justify-center gap-2"
          >
            <span className="text-white text-sm font-medium whitespace-nowrap">Contact us directly with</span>
            <img src={WhatsAppLogo} alt="WhatsApp" className="h-5 w-5 object-contain flex-shrink-0" />
          </a>

          {/* Primary button */}
          <button
            type="button"
            onClick={handleContinue}
            disabled={disabled}
            className="w-full py-3 px-6 rounded-full md:rounded-lg transition inline-flex items-center justify-center text-sm font-medium bg-white text-mm_black hover:bg-gray-100 disabled:bg-gray-600 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Continue with phone number'}
          </button>

          {/* Disclaimer */}
          <p className="text-center text-xs text-gray-500">
            By continuing, you agree to our{' '}
            <a href="/documents/PrivacyNotice" className="underline hover:text-gray-400">Privacy Policy</a>{' '}
            and{' '}
            <a href="/documents/TermsConditions" className="underline hover:text-gray-400">Terms of Service</a>
          </p>
        </div>
      </div>
    </>
  );
}

// ============================================
// EMAIL VIEW COMPONENT
// ============================================

function EmailView({ onSwitchToPhone, viewReportId, reportRequestId }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  const isValid = useMemo(() => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return re.test(String(email || '').trim());
  }, [email]);

  const handleContinue = useCallback(async () => {
    setTouched(true);
    if (!isValid || isSubmitting) return;
    
    setIsSubmitting(true);
    const cleanEmail = String(email).trim();
    
    console.log('[ContactInfoPage] 📧 EMAIL SUBMISSION STARTED:', {
      email: cleanEmail,
      viewReportId,
      reportRequestId
    });

    // Track to both systems in parallel
    const [trackResult, firestoreResult] = await Promise.all([
      // 1. Track to session events (ALWAYS)
      trackContactSubmission({
        viewReportId,
        reportRequestId,
        contactType: 'email',
        emailAddress: cleanEmail,
        source: 'contact_info_page_email'
      }),
      // 2. Write to ViewReport document (if we have IDs)
      viewReportId && reportRequestId
        ? writeContactToViewReport({
            viewReportId,
            reportRequestId,
            contactType: 'email',
            emailAddress: cleanEmail,
            source: 'contact_info_page_email'
          })
        : Promise.resolve({ success: false, skipped: true })
    ]);

    console.log('[ContactInfoPage] 📧 EMAIL SUBMISSION COMPLETE:', {
      trackResult,
      firestoreResult
    });

    // Track pixel Lead events for email submission
    trackMetaLead({ content_name: 'contact_info_page_email', content_category: 'view_report_contact' });
    trackTikTokContact('view_report_email');

    // Wait for async queue to flush before navigating
    await new Promise(resolve => setTimeout(resolve, 500));

    console.log('[ContactInfoPage] ✅ NAVIGATING TO HOMEPAGE');
    navigate('/');
    // Note: isSubmitting stays true to prevent double-clicks during navigation
  }, [email, isValid, isSubmitting, viewReportId, reportRequestId, navigate]);

  return (
    <>
      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="w-full lg:w-2/5 mx-auto px-5 sm:px-6 pt-6 pb-48">
          {/* Title & Subtitle */}
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 leading-tight">
            Please share your email
          </h1>
          <p className="text-gray-400 text-sm mb-8">
            We need your contact information to facilitate the process with the appropriate clinics.
          </p>

          {/* Email Input */}
          <label className="block mb-2 text-sm font-medium text-white">
            Your email
          </label>
          <input
            ref={inputRef}
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="Email address"
            className="px-4 py-2 rounded-xl border w-full outline-none text-white bg-transparent border-gray-600 placeholder-gray-500 focus:ring-2 focus:ring-white transition"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleContinue();
              }
            }}
            disabled={isSubmitting}
          />
          {!isValid && touched && (
            <div className="mt-2 text-sm text-red-500">Please enter a valid email address.</div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-mm_black z-50">
        <div className="w-full lg:w-2/5 mx-auto px-5 sm:px-6 pt-4 pb-6 space-y-3">
          <p className="text-center text-xs text-gray-400">
            Up to <span className="font-semibold">4x faster</span> responses with phone number contacts
          </p>
          
          {/* Secondary: Switch to phone */}
          <button
            type="button"
            onClick={onSwitchToPhone}
            disabled={isSubmitting}
            className="w-full py-3 px-6 rounded-full md:rounded-lg border text-white border-white bg-[#1f1f1f] hover:bg-black transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Enter phone number instead
          </button>

          {/* Primary button */}
          <button
            type="button"
            onClick={handleContinue}
            disabled={!isValid || isSubmitting}
            className="w-full py-3 px-6 rounded-full md:rounded-lg transition inline-flex items-center justify-center text-sm font-medium bg-white text-mm_black hover:bg-gray-100 disabled:bg-gray-600 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Continue'}
          </button>

          {/* Disclaimer */}
          <p className="text-center text-xs text-gray-500">
            By continuing, you agree to our{' '}
            <a href="/documents/PrivacyNotice" className="underline hover:text-gray-400">Privacy Policy</a>{' '}
            and{' '}
            <a href="/documents/TermsConditions" className="underline hover:text-gray-400">Terms of Service</a>
          </p>
        </div>
      </div>
    </>
  );
}

// ============================================
// MAIN PAGE COMPONENT
// ============================================

export default function ContactInfoPage() {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState('phone'); // 'phone' | 'email'

  // Extract viewReportId and reportRequestId from URL params
  const viewReportId = searchParams.get('vr') || searchParams.get('viewReportId');
  const reportRequestId = searchParams.get('rid') || searchParams.get('reportRequestId');

  // Log on mount for debugging
  useEffect(() => {
    console.log('[ContactInfoPage] 🚀 MOUNTED with params:', {
      viewReportId,
      reportRequestId,
      fullUrl: window.location.href
    });

    // Initialize tracking session
    Tracking.initializeSession().then((sessionId) => {
      console.log('[ContactInfoPage] Tracking session:', sessionId);
    });
  }, [viewReportId, reportRequestId]);

  return (
    <div className="min-h-screen flex flex-col bg-mm_black">
      <Helmet>
        <title>Contact Information | Match Medic</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-mm_black border-b border-gray-800">
        <div className="w-full lg:w-2/5 mx-auto px-5 sm:px-6 py-3 flex items-center justify-center">
          <img src={matchMedicLogo} alt="Match Medic" className="h-6" />
        </div>
      </header>

      {view === 'phone' ? (
        <PhoneView 
          onSwitchToEmail={() => setView('email')} 
          viewReportId={viewReportId}
          reportRequestId={reportRequestId}
        />
      ) : (
        <EmailView 
          onSwitchToPhone={() => setView('phone')} 
          viewReportId={viewReportId}
          reportRequestId={reportRequestId}
        />
      )}
    </div>
  );
}
